import { useEffect, useState } from "react";
import type { DefaultOptionType } from "antd/es/select";
import useSWR from "swr";
import { Channel as ApiChannel, Factor, Metric } from "../../../api/types";
import urls from "../../../api/urls";
import { api } from "../../../api/apiProvider";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { Engine } from "../../../api/types";

interface ChannelFactor {
  description: string;
  engine: string;
  id: number;
  isChannel: boolean;
  isActive: boolean;
  metric: DefaultOptionType | null;
  name: string;
  audienceText: string;
  managerText: string;
}

interface Channel {
  id: number | null;
  name: string;
  audienceText: string;
  managerText: string;
  '@id': string;
  factors: ChannelFactor[];
}

const updateEngineFactors = async (id: number, data: any) =>
  await api.patch(`${urls.engines}/${id}/configure`, { arg: data });

export function useData(engine: Engine) {
  const { t } = useTranslation(); // Теперь хук использует переводы внутри себя
  const engineId = engine.id;
  const [metricsOptions, setMetricsOptions] = useState<DefaultOptionType[]>([]);
  const [channelsOptions, setChannelsOptions] = useState<DefaultOptionType[]>([]);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isValuesChanged, setIsValuesChanged] = useState(false);
  const [isFactorsSaving, setIsFactorsSaving] = useState(false);
  const [isChannelModalVisible, setIsChannelModalVisible] = useState(false);
  const [pendingFactor, setPendingFactor] = useState<{
    channelIndex: number;
    factorIndex: number;
  } | null>(null);
  const [selectedChannelIri, setSelectedChannelIri] = useState<string | undefined>(undefined);

  const { data: allChannelsData } = useSWR<{ data: ApiChannel[] }>(
    `${urls.engineChannels}?engine.id=${engineId}&pagination=false`,
    api.get,
    { revalidateOnFocus: false }
  );

  const { data: factorsData, mutate: mutateFactors } = useSWR<{
    data: Factor[];
  }>(
    `${urls.engineFactors}?engine.id=${engineId}&t[]=factor&t[]=metrics&t[]=channel&t[]=dataChannel`,
    api.get,
    { revalidateOnFocus: false }
  );

  const { data: metrics } = useSWR<{
    data: Metric[]
  }>(`${urls.metrics}/?exists[foreignId]=true`, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (allChannelsData?.data) {
      setChannelsOptions(
        allChannelsData?.data.map((channel: ApiChannel) => ({
          value: channel['@id'],
          label: channel.name,
        }))
      );
    }
  }, [allChannelsData]);

  useEffect(() => {
    if (metrics?.data) {
      setMetricsOptions(
        metrics.data.map((metric: Metric) => ({
          value: metric['@id'],
          label: metric.name,
        }))
      );
    }
  }, [metrics]);

  useEffect(() => {
    if (factorsData?.data) {
      setFactors(factorsData?.data);
    }
  }, [factorsData]);

  useEffect(() => {
    if (!factors) return;

    const channelsMap: Record<string, Channel> = {};

    for (const factor of factors) {
      const key = factor.channel ? String(factor.channel['@id']) : 'null';
      if (!channelsMap[key]) {
        channelsMap[key] = {
          id: factor.channel ? Number(factor.channel.id) : null,
          '@id': factor.channel ? factor.channel['@id'] : '',
          name: factor.channel ? factor.channel.name : t('factors.nonChannel'),
          factors: [],
          managerText: '',
          audienceText: factor.channel?.audienceText || factor.audienceText || '',
        };
      }

      channelsMap[key].factors.push({
        description: factor.description || '',
        engine: String(factor.engine),
        id: factor.id,
        isChannel: factor.isChannel,
        isActive: factor.isActive ?? true,
        metric: factor.metric
          ? { label: factor.metric.name, value: factor.metric['@id'] }
          : null,
        name: factor.name,
        managerText: factor.managerText || factor.channel?.managerText || '',
        audienceText: factor.audienceText || factor.channel?.audienceText || '',
      });
    }

    const channelsArray = Object.values(channelsMap);
    setChannels(channelsArray);
  }, [factors, t]);

  const performMove = (
    fromChannelIndex: number,
    fromFactorIndex: number,
    targetChannelIri: string | null
  ) => {
    setChannels(prev => {
      const newChannels = prev.map(ch => ({ ...ch, factors: [...ch.factors] }));

      const factor = { ...newChannels[fromChannelIndex].factors[fromFactorIndex] };
      factor.isChannel = targetChannelIri !== null;

      newChannels[fromChannelIndex].factors.splice(fromFactorIndex, 1);

      let targetChannel: Channel;

      if (targetChannelIri === null) {
        targetChannel = newChannels.find(ch => ch.id === null) ?? {
          id: null,
          '@id': '',
          name: t('factors.nonChannel'),
          factors: [],
          audienceText: '',
          managerText: '',
        };
        if (!newChannels.some(ch => ch.id === null)) {
          newChannels.push(targetChannel);
        }
      } else {
        targetChannel = newChannels.find(ch => ch['@id'] === targetChannelIri) ?? {
          id: Number(targetChannelIri.split('/').pop() || 0),
          '@id': targetChannelIri,
          name: channelsOptions.find(opt => opt.value === targetChannelIri)?.label as string || 'Unknown',
          factors: [],
          audienceText: '',
          managerText: '',
        };
        if (!newChannels.some(ch => ch['@id'] === targetChannelIri)) {
          newChannels.push(targetChannel);
        }
      }

      targetChannel.factors.push(factor);

      targetChannel.managerText = targetChannel.factors[0]?.managerText || '';

      setChannels(newChannels);
      setIsValuesChanged(true);

      const newChannelIndex = newChannels.findIndex(ch =>
        targetChannelIri === null ? ch.id === null : ch['@id'] === targetChannelIri
      );

      if (newChannelIndex === -1) {
        return newChannels;
      }

      const newFactorIndex = newChannels[newChannelIndex].factors.length - 1;
      const newKey = `${newChannelIndex}-${newFactorIndex}`;

      setTimeout(() => {
        const rowSelector = `tr[data-row-key="${newKey}"]`;
        const row = document.querySelector(rowSelector) as HTMLTableRowElement | null;

        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          row.classList.add('highlight-row');
          setTimeout(() => row.classList.remove('highlight-row'), 1500);
        }
      }, 150);

      setIsValuesChanged(true);

      return newChannels;
    });
  };

  const moveFactor = (channelIndex: number, factorIndex: number, newIsChannel: boolean) => {
    if (!newIsChannel) {
      performMove(channelIndex, factorIndex, null);
      return;
    }

    setPendingFactor({ channelIndex, factorIndex });
    setSelectedChannelIri(undefined);
    setIsChannelModalVisible(true);
  };

  const handleChannelSelectOk = () => {
    if (!pendingFactor || !selectedChannelIri) {
      message.warning(t('common.choose'));
      return;
    }
    performMove(pendingFactor.channelIndex, pendingFactor.factorIndex, selectedChannelIri);
    setIsChannelModalVisible(false);
    setPendingFactor(null);
  };

  const handleChannelSelectCancel = () => {
    setIsChannelModalVisible(false);
    setPendingFactor(null);
  };

  const onSave = async (values: { channels: Channel[] }) => {
    setIsFactorsSaving(true);

    const updatedChannels = channels.map((originalChannel, idx) => {
      const formChannel = values.channels[idx];

      if (!formChannel) {
        return originalChannel;
      }

      const updatedChannel: Channel = {
        ...originalChannel,
        audienceText: formChannel.audienceText ?? originalChannel.audienceText,
        managerText: formChannel?.factors[0]?.managerText || '',
      };

      updatedChannel.factors = originalChannel.factors.map((originalFactor, fIdx) => {
        const formFactor = formChannel.factors?.[fIdx];

        if (!formFactor) {
          return originalFactor;
        }

        return {
          ...originalFactor,
          description: formFactor.description ?? originalFactor.description,
          metric: formFactor.metric ?? originalFactor.metric,
          managerText: formFactor.managerText ?? originalFactor.managerText,
          isActive: formFactor.isActive ?? originalFactor.isActive,
          isChannel: formFactor.isChannel ?? originalFactor.isChannel,
        };
      });

      return updatedChannel;
    });

    const payload = {
      channels: updatedChannels.map(channel => {
        const hasId = typeof channel.id === 'number' && channel.id > 0;
        return {
          id: channel.id ?? null,
          audienceText: hasId ? channel.audienceText : undefined,
          managerText: hasId && channel.managerText ? channel.managerText : undefined,
          factors: channel.factors.map(factor => ({
            id: factor.id,
            description: factor.description,
            isChannel: factor.isChannel,
            isActive: factor.isActive,
            metric: factor.metric?.value ?? null,
            audienceText: !hasId ? channel.audienceText : undefined,
            managerText: !hasId ? factor.managerText : undefined,
          })),
        };
      }),
    };

    try {
      await updateEngineFactors(engineId, payload);
      message.success(t('common.saved'));
      setIsValuesChanged(false);
      setChannels(updatedChannels);
      mutateFactors();
    } catch (err: any) {
      if (err?.['hydra:description']) {
        message.error(err['hydra:description']);
      }
    } finally {
      setIsFactorsSaving(false);
    }
  };

  const downloadJson = () => {
    if (!engine.json) {
      return;
    }

    const blob = new Blob([engine.json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `model_${engineId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return {
    metricsOptions,
    channelsOptions,
    factors,
    mutateFactors,
    channels,
    isValuesChanged,
    isFactorsSaving,
    isChannelModalVisible,
    selectedChannelIri,
    setSelectedChannelIri,
    moveFactor,
    handleChannelSelectOk,
    handleChannelSelectCancel,
    onSave,
    downloadJson,
    setIsValuesChanged,
  };
}
